import {
  LayerType,
  getHexColor,
  getIngTypeColorByIngIngTypeId,
  GetIngredientTypeColors,
  SetIngredientTypeColors,
  ingredientTypeByTypeId,
} from '../../../utils/Global';
import { getUniqueLayerName } from '../../../business/plateviewer/GenerateUtil';
import {
  LayerTarget,
  TargetLayer,
  TargetLayerIngredient,
} from '../../../business/models/CustomModels';
import {
  ConditionDTO,
  ConditionListLayer,
  CustomLayerDTO,
  IngredientTypeColorDTO,
  Layer,
  LayerDTO,
  WellIngDTO,
} from '../../../business/models/Models';
import ExperimentDtoClient from '../../../business/models/ExperimentDtoClient';
import {
  createTargetLayer,
  CreateTargetLayerIngredient,
  findWellsInLayer,
  getIngredient,
  getIngStock,
  IsBuffer,
  RemoveWellIngredientsForOverrideLayer,
  updateDropByTargetLayer,
  updateWellByTargetLayer,
} from './GenerateCommon';
import { Compute } from './VolCalculator';

export function CustomLayerGenerate(docData: ExperimentDtoClient, lyr: LayerDTO) {
  let customLayer: CustomLayerDTO;
  if (lyr.type != LayerType.ConditionList) {
    throw new Error('CustomLayerGenerate: Invalid Layer Type.');
  } else {
    customLayer = lyr.layerDetail as CustomLayerDTO;
  }
  const layerHeight = lyr.height + 1;
  const layerWidth = lyr.width + 1;

  const wells = findWellsInLayer(
    docData.plateDto,
    lyr,
    docData.containerRows,
    docData.containerCols,
  );

  for (let i = 0; i < layerHeight; i++) {
    for (let j = 0; j < layerWidth; j++) {
      const well: WellIngDTO = wells[i][j];
      RemoveWellIngredientsForOverrideLayer(docData, lyr, well);

      const conditionNumber = i * layerWidth + j + 1;
      const conditionRow = FindByConditionNumber(customLayer, conditionNumber);

      // leave the well if no condition is found.
      if (conditionRow == null) {
        continue;
      }

      const targetLayer: TargetLayer = createTargetLayer(lyr, well);
      targetLayer.volume = customLayer.wellVolume;
      targetLayer.waterVolume = 0;
      targetLayer.sourceWellConditionNumber = conditionNumber.toString();
      GenerateWellLayerIngredients(customLayer, well, conditionRow, targetLayer);
      Compute(targetLayer, lyr, docData);
      if (lyr.targetDrop < 0) {
        updateWellByTargetLayer(lyr, targetLayer, well);
        well.wellWaterVolume += targetLayer.waterVolume;
      } else {
        const drop = updateDropByTargetLayer(lyr, targetLayer, well);
        if (drop) {
          drop.volume += targetLayer.volume;
          drop.waterVolume += targetLayer.waterVolume;
        }
      }
    }
  }
}

export function GetLayerTarget(targetWell: WellIngDTO, layer: Layer): LayerTarget {
  let target: LayerTarget;
  if (layer.targetDropNumber >= 0) {
    if (targetWell.dropList === undefined) {
      throw 'Target drop No. not found exception';
    }

    target = targetWell.dropList.find((d) => d.dropNumber === layer.targetDropNumber);

    if (target === undefined) {
      throw 'Target drop No. not found exception';
    }
  } else if (layer.targetDropNumber < 0) {
    target = targetWell;
  }
  return target;
}

export function FindByConditionNumber(
  condListLayer: CustomLayerDTO,
  conditionNumber: number,
): ConditionDTO {
  const condition: ConditionDTO = condListLayer.condition.find(
    (cond) =>
      cond.conditionListLayerId == condListLayer.id &&
      cond.conditionNumber == conditionNumber,
  );
  return condition;
}

export function CreateTargetLayer(lyr: LayerDTO, well: WellIngDTO): TargetLayer {
  const targetLayer = new TargetLayer();
  return targetLayer;
}

export function GenerateWellLayerIngredients(
  condListLayerRow: ConditionListLayer,
  wellRow: WellIngDTO,
  conditionRow: ConditionDTO,
  targetLayer: TargetLayer,
) {
  const globalIngredientTypeColors: IngredientTypeColorDTO[] = [
    ...GetIngredientTypeColors(),
  ];
  for (const condIngRow of conditionRow.conditionIngredient) {
    // skip over new ingredients where ing ing type hasn't been selected
    if (
      condIngRow.ingredientIngredientTypeId === null ||
      condIngRow.ingredientIngredientTypeId === 0
    ) {
      continue;
    }

    const ingredientTypeColor: IngredientTypeColorDTO = getIngTypeColorByIngIngTypeId(
      condIngRow.ingredientIngredientTypeId,
    );
    globalIngredientTypeColors.push(ingredientTypeColor);
    SetIngredientTypeColors(globalIngredientTypeColors);
    const targetLayerIngredient: TargetLayerIngredient =
      CreateTargetLayerIngredient(targetLayer);
    targetLayerIngredient.ingredientIngredientTypeId =
      condIngRow.ingredientIngredientTypeId;
    targetLayerIngredient.ingredientStockId = condIngRow.ingredientStockId;
    targetLayerIngredient.ingredientStock = getIngStock(condIngRow.ingredientStockId);
    targetLayerIngredient.ingredientStockIngredient = getIngredient(
      targetLayerIngredient.ingredientStock?.ingredientId,
    );
    targetLayerIngredient.preMixedConcentration = 0;
    targetLayerIngredient.color = ingredientTypeColor.color;
    targetLayerIngredient.hexColor = getHexColor(ingredientTypeColor.color);
    targetLayerIngredient.ingredientTypeColorGroupId = ingredientTypeColor.groupId;
    const ingIngType = ingredientTypeByTypeId(condIngRow.ingredientIngredientTypeId);
    targetLayerIngredient.ingredientTypeName = ingIngType.name;
    targetLayerIngredient.ingredientTypeId = ingIngType.id;

    //Refactor: Remove the following line
    targetLayerIngredient.concentration = 0;

    targetLayerIngredient.isBuffer = ingIngType.isBuffer;
    targetLayerIngredient.ingredientTypeIsBuffer = ingIngType.isBuffer;
    targetLayerIngredient.ingredientIngredientTypeIsBuffer = ingIngType.isBuffer;

    if (targetLayerIngredient.preMixedConcentration != condIngRow.concentration) {
      targetLayerIngredient.preMixedConcentration = condIngRow.concentration;
    }

    if (IsBuffer(condIngRow.ingredientIngredientTypeId)) {
      if (!targetLayerIngredient.pH || targetLayerIngredient.pH != condIngRow.pH) {
        targetLayerIngredient.pH = condIngRow.pH;
      }
      // May be converting a well ingredient row from a single vial buffer to a dual vial.
      // Need to update the row, but only dirty it if absolutely necessary.
      if (condIngRow.highPhingredientStockId == null) {
        if (targetLayerIngredient.highPhIngStockId != null) {
          targetLayerIngredient.highPhIngStockId = null;
        }
      } else {
        if (
          targetLayerIngredient.highPhIngStockId == null ||
          targetLayerIngredient.highPhIngStockId != condIngRow.highPhingredientStockId
        ) {
          targetLayerIngredient.highPhIngStockId = condIngRow.highPhingredientStockId;
          targetLayerIngredient.highPhIngStock = getIngStock(
            condIngRow.highPhingredientStockId,
          );
        }
      }
    }
    //No need to push explicitly, createTargetLayerIngredient already did that
    //targetLayer.targetLayerIngredientList.push(targetLayerIngredient);
  }
  SetIngredientTypeColors([...globalIngredientTypeColors]);
}

export async function createNewCustomLayer(
  docData: ExperimentDtoClient,
  target: number,
): Promise<LayerDTO> {
  if (docData.layers === undefined) {
    docData.layers = [];
  }

  let layerVolume = docData.defWellVolume;
  if (target > 0) {
    layerVolume = docData.containerDefAdditiveVolume;
  }

  const baseLayerName = 'Custom Layer';
  const layerName = await getUniqueLayerName(docData.layers, baseLayerName);
  const newLayer = new LayerDTO();
  newLayer.layerDetail = new CustomLayerDTO();
  newLayer.name = layerName;
  newLayer.expId = docData.expId;
  newLayer.volume = layerVolume;
  newLayer.targetDrop = target;
  newLayer.x = 0;
  newLayer.y = 0;
  newLayer.width = docData.containerCols - 1;
  newLayer.height = docData.containerRows - 1;
  newLayer.type = LayerType.ConditionList;
  newLayer.startingWellNumber = 1;
  newLayer.volumeBasedAddition = true;
  newLayer.dispenseManually = false;

  docData.layers.push(newLayer);
  return newLayer;
}
